import React, { useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
// =============================================================================
// LEAD CAPTURE INTEGRATION POINT
// =============================================================================
// This is the only thing you need to set up to capture leads.
// Replace the URL below with your GoHighLevel (GHL) webhook URL,
// Zapier hook, Make.com hook, or any HTTPS endpoint that accepts JSON.
//
// In GHL: Settings → Integrations → Inbound Webhooks → copy the URL
// =============================================================================
const LEAD_WEBHOOK_URL = ''; // <-- PASTE YOUR GHL WEBHOOK URL HERE
async function submitLead(payload: Record<string, unknown>) {
  if (!LEAD_WEBHOOK_URL) {
    console.warn('[Watchdog] LEAD_WEBHOOK_URL is not set in components/LeadForm.tsx. Lead payload:', payload);
    return {
      ok: true,
      demo: true
    };
  }
  try {
    const res = await fetch(LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...payload,
        source: 'watchdogsecurity.com',
        submittedAt: new Date().toISOString()
      })
    });
    return {
      ok: res.ok,
      demo: false
    };
  } catch (err) {
    console.error('[Watchdog] Lead submit failed', err);
    return {
      ok: false,
      demo: false
    };
  }
}
type LeadFormProps = {
  variant?: 'card' | 'inline';
  defaultInterest?: string;
  title?: string;
  subtitle?: string;
};
export function LeadForm({
  variant = 'card',
  defaultInterest = 'General inquiry',
  title = 'Request your free assessment',
  subtitle = 'Tell us a bit about your property and we will reach out within one business day.'
}: LeadFormProps) {
  const [form, setForm] = useState({
    name: '',
    business: '',
    phone: '',
    email: '',
    interest: defaultInterest,
    notes: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handle = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm({
    ...form,
    [key]: e.target.value
  });
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    const res = await submitLead(form);
    setStatus(res.ok ? 'success' : 'error');
  };
  if (status === 'success') {
    return <div className={`rounded-[2rem] bg-white p-10 text-center shadow-2xl shadow-sky-100 ring-1 ring-slate-200 ${variant === 'inline' ? '' : ''}`} data-id="element-108">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600" data-id="element-109">
          <CheckCircle2 className="h-8 w-8" data-id="element-110" />
        </div>
        <h3 className="text-2xl font-black" data-id="element-111">Request received</h3>
        <p className="mt-3 leading-7 text-slate-600" data-id="element-112">
          Thanks {form.name.split(' ')[0] || 'there'} — a Watchdog specialist
          will reach out shortly. For urgent issues call{' '}
          <a href="tel:+19168424729" className="font-black text-sky-600" data-id="element-113">
            (916) 842-4729
          </a>
          .
        </p>
      </div>;
  }
  return <form onSubmit={onSubmit} className="rounded-[2rem] bg-white p-6 shadow-2xl shadow-sky-100 ring-1 ring-slate-200" data-id="element-114">
      <h3 className="text-2xl font-black" data-id="element-115">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-116">{subtitle}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-117">
        <input required value={form.name} onChange={handle('name')} placeholder="Full name *" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-118" />
        <input value={form.business} onChange={handle('business')} placeholder="Business name" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-119" />
        <input required value={form.phone} onChange={handle('phone')} placeholder="Phone *" type="tel" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-120" />
        <input value={form.email} onChange={handle('email')} placeholder="Email" type="email" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-121" />
        <select value={form.interest} onChange={handle('interest')} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4 sm:col-span-2" data-id="element-122">
          <option data-id="element-123">General inquiry</option>
          <option data-id="element-124">Access Control</option>
          <option data-id="element-125">Video Surveillance</option>
          <option data-id="element-126">Intrusion Detection</option>
          <option data-id="element-127">Smart Home Security</option>
          <option data-id="element-128">Repairs and Upgrades</option>
          <option data-id="element-129">Multi Site Management</option>
        </select>
        <textarea value={form.notes} onChange={handle('notes')} placeholder="Tell us about your property or main concern" rows={4} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4 sm:col-span-2" data-id="element-130" />
      </div>

      {status === 'error' && <p className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600" data-id="element-131">
          Please add your name and phone number, or call us directly at (916)
          842-4729.
        </p>}

      <button type="submit" disabled={status === 'loading'} className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-5 text-sm font-black text-white transition hover:bg-slate-800 disabled:opacity-60" data-id="element-132">
        {status === 'loading' ? 'Sending…' : 'Request My Free Assessment'}
        <ArrowRight className="h-4 w-4" data-id="element-133" />
      </button>

      <p className="mt-4 text-center text-xs font-medium text-slate-500" data-id="element-134">
        We respect your privacy. No spam, ever.
      </p>
    </form>;
}